// src/quality-management/components/DeliveryTaskTable.tsx
// Tabela detalhada do Plano de Entregas: mesmas tarefas exibidas no gráfico,
// com datas, responsáveis, predecessora, duração, horas e situação de prazo.

import React, { useMemo } from 'react';
import { Task } from '../services/api/types';
import { Table } from '../../shared/components/ui';
import type { Column } from '../../shared/components/ui';
import { getTaskRange, taskScheduleState, SCHEDULE_STYLES, formatDayBR } from '../utils/taskSchedule';

const DAY = 86_400_000;

interface DeliveryTaskTableProps {
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
}

const formatDate = (iso?: string | null): string => (iso ? formatDayBR(iso) : '—');

// Duração em dias corridos (início e fim inclusos)
const durationDays = (task: Task): string => {
  const { start, end } = getTaskRange(task);
  if (!start || !end) return '—';
  const days = Math.round((end.getTime() - start.getTime()) / DAY) + 1;
  return `${days} ${days === 1 ? 'dia' : 'dias'}`;
};

const DeliveryTaskTable: React.FC<DeliveryTaskTableProps> = ({ tasks, onTaskClick }) => {
  const now = useMemo(() => new Date(), []);

  // Ordena por projeto e, dentro do projeto, por data de início
  const sorted = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const pa = a.project?.name || `Projeto ${a.project?.id ?? a.project_id}`;
      const pb = b.project?.name || `Projeto ${b.project?.id ?? b.project_id}`;
      const byProject = pa.localeCompare(pb, 'pt-BR');
      if (byProject !== 0) return byProject;
      const sa = getTaskRange(a).start?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const sb = getTaskRange(b).start?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return sa - sb;
    });
  }, [tasks]);

  const columns: Column<Task>[] = [
    {
      key: 'name',
      header: 'Tarefa',
      render: (t) => (
        <div className="min-w-[160px]">
          <span className="font-medium text-foreground">{t.name}</span>
          {t.description && (
            <p className="mt-0.5 max-w-[280px] truncate text-xs text-foreground-muted" title={t.description}>
              {t.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'project',
      header: 'Projeto',
      render: (t) => (
        <span className="text-sm text-foreground-muted">
          {t.project?.name || `Projeto ${t.project?.id ?? t.project_id}`}
        </span>
      ),
    },
    {
      key: 'employees',
      header: 'Responsáveis',
      render: (t) =>
        t.employees && t.employees.length > 0 ? (
          <span className="text-sm">{t.employees.map((e) => e.name).join(', ')}</span>
        ) : (
          <span className="text-sm text-foreground-subtle">—</span>
        ),
    },
    {
      key: 'predecessor',
      header: 'Predecessora',
      render: (t) =>
        t.predecessor ? (
          <span className="text-sm">{t.predecessor.name || t.predecessor.title}</span>
        ) : (
          <span className="text-sm text-foreground-subtle">—</span>
        ),
    },
    {
      key: 'start',
      header: 'Início',
      align: 'center',
      render: (t) => <span className="text-sm tabular-nums">{formatDate(t.task_date)}</span>,
    },
    {
      key: 'end',
      header: 'Fim',
      align: 'center',
      render: (t) => <span className="text-sm tabular-nums">{formatDate(t.due_date)}</span>,
    },
    {
      key: 'duration',
      header: 'Duração',
      align: 'center',
      render: (t) => <span className="text-sm tabular-nums">{durationDays(t)}</span>,
    },
    {
      key: 'expected_hours',
      header: 'Horas prev.',
      align: 'center',
      render: (t) => (
        <span className="text-sm tabular-nums">{t.expected_hours != null ? `${t.expected_hours}h` : '—'}</span>
      ),
    },
    {
      key: 'progress',
      header: 'Progresso',
      width: '140px',
      render: (t) => {
        const progress = Math.min(Math.max(t.completion_percentage ?? 0, 0), 100);
        return (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-3">
              <div
                className={`h-full rounded-full ${progress === 100 ? 'bg-success' : 'bg-brand'}`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs tabular-nums text-foreground-muted">{progress}%</span>
          </div>
        );
      },
    },
    {
      key: 'schedule',
      header: 'Situação',
      render: (t) => {
        const style = SCHEDULE_STYLES[taskScheduleState(t, now)];
        return (
          <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${style.text}`}>
            <span className={`h-2 w-2 rounded-full ${style.bar}`} />
            {style.label}
          </span>
        );
      },
    },
  ];

  return (
    <Table<Task>
      columns={columns}
      data={sorted}
      rowKey={(t) => t.id}
      onRowClick={onTaskClick}
      empty="Nenhuma tarefa para os filtros selecionados."
    />
  );
};

export default DeliveryTaskTable;
